<template>
  <el-card class="birthday-widget" shadow="hover" v-loading="loading">
    <template #header>
      <div class="widget-header">
        <span>🎂 생일 반차</span>
      </div>
    </template>

    <div v-if="!activeGrant && !user.birth_date" class="empty-state">
      <el-alert type="info" :closable="false" show-icon>
        <template #title>마이페이지에서 생년월일을 등록하시면 생일 반차가 자동 부여됩니다 🎂</template>
      </el-alert>
      <div style="margin-top: 8px;">
        <el-button size="small" type="primary" @click="$emit('register-birthday')">
          생일 등록하러 가기
        </el-button>
      </div>
    </div>

    <div v-else-if="activeGrant" :class="['grant-card', cardClass]">
      <div class="grant-row">
        <span class="label">사용 가능</span>
        <span class="value">{{ activeGrant.valid_from }} ~ {{ activeGrant.valid_to }}</span>
      </div>
      <div class="grant-row">
        <span class="label">잔여</span>
        <span class="value">
          <strong>D-{{ Math.max(activeGrant.days_remaining, 0) }}</strong>
          <span v-if="activeGrant.days_remaining <= 2" style="color: var(--el-color-warning); margin-left:4px;">곧 만료</span>
        </span>
      </div>
      <div class="grant-row">
        <span class="label">부여</span>
        <span class="value">{{ activeGrant.days }}일 (반차)</span>
      </div>
      <div class="grant-row">
        <span class="label">상태</span>
        <span class="value">
          <el-tag :type="statusType">{{ statusLabel }}</el-tag>
        </span>
      </div>
      <div style="margin-top:12px;">
        <el-button
          v-if="activeGrant.status === 'GRANTED' && activeGrant.is_usable_today"
          type="primary" @click="goApply">
          지금 신청하기
        </el-button>
        <span v-else-if="activeGrant.status === 'REQUESTED'" style="color:#909399;">결재 대기 중</span>
        <span v-else-if="activeGrant.status === 'USED'" style="color:#67c23a;">사용 완료</span>
      </div>
    </div>

    <div v-else class="empty-state">
      <el-empty description="현재 사용 가능한 생일 반차가 없습니다" :image-size="60">
        <template #description>
          <span v-if="user.birth_date">
            다음 생일에 자동으로 부여됩니다 ({{ user.birth_date_md }})
          </span>
          <span v-else>생일 미등록</span>
        </template>
      </el-empty>
    </div>
  </el-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axiosInstance from '@/api/axios'

defineEmits(['register-birthday'])

const loading = ref(false)
const activeGrant = ref(null)
const history = ref([])
const user = ref({ birth_date: null })

const statusLabel = computed(() => {
  if (!activeGrant.value) return ''
  return {
    GRANTED: '사용 가능',
    REQUESTED: '결재 대기',
    USED: '사용 완료',
    EXPIRED: '만료',
    CANCELLED: '취소',
  }[activeGrant.value.status] || activeGrant.value.status
})

const statusType = computed(() => {
  if (!activeGrant.value) return 'info'
  return {
    GRANTED: 'success',
    REQUESTED: 'primary',
    USED: 'info',
    EXPIRED: 'danger',
    CANCELLED: 'warning',
  }[activeGrant.value.status] || 'info'
})

const cardClass = computed(() => {
  if (!activeGrant.value) return ''
  if (activeGrant.value.status === 'EXPIRED' || activeGrant.value.status === 'CANCELLED') return 'grant-card--muted'
  if (activeGrant.value.days_remaining <= 2 && activeGrant.value.status === 'GRANTED') return 'grant-card--warning'
  if (activeGrant.value.status === 'REQUESTED') return 'grant-card--pending'
  if (activeGrant.value.status === 'USED') return 'grant-card--muted'
  return 'grant-card--active'
})

async function fetchActive() {
  loading.value = true
  try {
    const r = await axiosInstance.get('birthday-grants/my/active/')
    activeGrant.value = r.data.active
    history.value = r.data.history || []
  } catch (e) {
    console.error('birthday-grant fetch error', e)
  } finally {
    loading.value = false
  }
}

async function fetchBirthday() {
  try {
    const r = await axiosInstance.get('me/birthday/')
    user.value = {
      ...r.data,
      birth_date_md: r.data.birth_date
        ? r.data.birth_date.slice(5)  // MM-DD
        : null,
    }
  } catch (e) {
    console.warn('birthday fetch', e)
  }
}

function goApply() {
  // 라우터로 휴가 신청 화면 이동, leave_type=생일반차 자동 선택
  window.location.href = '/leave/apply?type=birthday'
}

onMounted(() => {
  fetchActive()
  fetchBirthday()
})
</script>

<style scoped>
.birthday-widget {
  max-width: 420px;
}
.widget-header {
  font-weight: 700;
  font-size: 16px;
}
.grant-card {
  padding: 12px;
  border-radius: 8px;
  background: #f9f9f9;
}
.grant-card--active { background: #f0f9eb; border-left: 4px solid #67c23a; }
.grant-card--warning { background: #fdf6ec; border-left: 4px solid #e6a23c; }
.grant-card--pending { background: #ecf5ff; border-left: 4px solid #409EFF; }
.grant-card--muted { background: #f4f4f5; border-left: 4px solid #909399; }
.grant-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
}
.grant-row .label { color: #909399; }
.grant-row .value { color: #303133; }
.empty-state { text-align: center; padding: 12px 0; }
</style>
